import React, { useEffect, useState } from 'react';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import { drawerSections } from './content';
import { useMutation } from '@apollo/client';
import { updateCommissions } from './queries';
import { isEmpty } from 'lodash';
import { toast } from 'react-toastify';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { getError } from '@utils/graph-utils';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { isValidNumber } from '@utils/validationUtils';
import PrecisionService from '@services/PrecisionService';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';

const UpdateCommissionProposalDrawer: React.FC<
  IUpdateCommissionProposalDrawerProps
> = ({ id, open, onSuccess, onClose, data, partnerLov }) => {
  const [booted, setBooted] = useState<boolean>(false);

  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();
  const [inputsForm, setInputsForm] = useState<IDynamicFormWidgetSection[]>();

  const [updateCommissionAction] = useMutation(updateCommissions());

  const initialize = () => {
    const initalData = drawerSections(data, partnerLov);
    setInputsForm(initalData);
    setBooted(true);
  };

  useEffect(() => {
    if (open) {
      initialize();
    } else {
      setInputsForm(undefined);
    }
  }, [open]);

  const submitForm = async (values: Record<string, any>) => {
    const [data] = normalizeDynamicFormDrawerValues(inputsForm, values);
    setSubmitButtonState('loading');
    setFormDisabled(true);

    try {
      const variables = {
        entityId: id,
        primaryBP: data.primaryBP,
        primaryPercentage: isValidNumber(data.primaryPercentage)
          ? PrecisionService.divideBy100(data.primaryPercentage)
          : 0,
        secondaryBP: data.secondaryBP,
        secondaryPercentage: isEmpty(data.secondaryBP)
          ? null
          : isValidNumber(data.secondaryPercentage)
            ? PrecisionService.divideBy100(data.secondaryPercentage)
            : 0,
        additionalBP: data.additionalBP,
        additionalPercentage: isEmpty(data.additionalBP)
          ? null
          : isValidNumber(data.additionalPercentage)
            ? PrecisionService.divideBy100(data.additionalPercentage)
            : 0,
      };

      const res = await updateCommissionAction({
        variables: variables,
        errorPolicy: 'all',
      });

      if (isEmpty(res.errors)) {
        toast.success(
          <ToastSuccessMessage>
            {'Commission Split updated successfully'}
          </ToastSuccessMessage>
        );
        setTimeout(() => {
          setSubmitButtonState('success');
          onSuccess();
          onClose();
        }, 500);
      } else {
        setSubmitButtonState(undefined);
        toast.error(<ToastErrorMessage>{getError(res)}</ToastErrorMessage>);
      }
    } catch {
      setSubmitButtonState(undefined);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    } finally {
      setFormDisabled(false);
    }
  };

  if (!inputsForm) {
    return <></>;
  }

  return (
    <DynamicFormDrawer
      title="Modify Commission Split"
      onClose={() => onClose()}
      isOpen={open}
      loading={!booted}
      sections={inputsForm}
      onSubmit={(values) => submitForm(values)}
      submitButtonState={submitButtonState}
      disableForm={formDisabled}
    />
  );
};

export default UpdateCommissionProposalDrawer;
